import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Search, X, Tag as TagIcon, PackageSearch, ExternalLink, Sparkles, Loader2, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import ProductCard from '../components/ProductCard';
import { useSite } from '../context/SiteContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Ofertas = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [activeCat, setActiveCat] = useState(null); // null = Todas
  // AI search state
  const [aiSearched, setAiSearched] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResults, setAiResults] = useState([]);
  const [aiRelated, setAiRelated] = useState([]);
  const [aiExplanation, setAiExplanation] = useState('');
  const [aiCoupon, setAiCoupon] = useState(null);
  const [aiRecommendedId, setAiRecommendedId] = useState('');
  const [popularTerms, setPopularTerms] = useState([]);
  const { settings, darkMode } = useSite();

  // Admin-configurable offer categories
  const categories = useMemo(() => {
    try {
      const parsed = JSON.parse(settings.offer_categories || '[]');
      return Array.isArray(parsed) ? parsed.filter((c) => c && c.id && c.name) : [];
    } catch (e) {
      return [];
    }
  }, [settings.offer_categories]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/coupons?_t=${Date.now()}`);
      const now = new Date();
      const products = (res.data || [])
        .filter((c) => (c.item_kind || 'coupon') === 'product')
        .filter((c) => !c.expiration_date || new Date(c.expiration_date) >= now)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setItems(products);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Fetch popular searches when admin sets suggestions mode to "popular"
  useEffect(() => {
    if (settings.offers_suggestions_mode === 'popular') {
      axios.get(`${API}/offers/popular-searches`)
        .then((res) => setPopularTerms(res.data?.terms || []))
        .catch(() => setPopularTerms([]));
    }
  }, [settings.offers_suggestions_mode]);

  // Parse quick access buttons (shown in the Ofertas page)
  const quickButtons = useMemo(() => {
    try {
      return JSON.parse(settings.quick_access_buttons || '[]').filter((btn) => btn.active);
    } catch (e) {
      return [];
    }
  }, [settings.quick_access_buttons]);

  const effectivePrice = (p) => (p.offer_price != null ? p.offer_price : p.price);

  const matchesCategory = (p) => {
    if (!activeCat) return true;
    const cat = categories.find((c) => c.id === activeCat);
    if (!cat) return true;
    if (cat.mode === 'price') {
      const price = effectivePrice(p);
      return price != null && Number(price) <= Number(cat.maxPrice || 0);
    }
    return Array.isArray(p.offer_categories) && p.offer_categories.includes(cat.id);
  };

  // Default browsing list (no AI): hide search_only, apply category
  const browseList = items.filter((p) => !p.search_only).filter(matchesCategory);

  const handleSearch = async (e, forcedQuery) => {
    e?.preventDefault();
    const q = (forcedQuery !== undefined ? forcedQuery : searchInput).trim();
    if (!q) return;
    setSearchInput(q);
    setAiLoading(true);
    setAiSearched(true);
    setAiResults([]);
    setAiRelated([]);
    setAiExplanation('');
    setAiCoupon(null);
    setAiRecommendedId('');
    try {
      const res = await axios.post(`${API}/offers/smart-search`, { query: q });
      setAiResults(res.data?.offers || []);
      setAiRelated(res.data?.related || []);
      setAiExplanation(res.data?.explanation || '');
      setAiCoupon(res.data?.coupon_suggestion || null);
      setAiRecommendedId(res.data?.recommended_id || '');
    } catch (err) {
      console.error('AI search error:', err);
      setAiExplanation('Tuvimos un problema al buscar. Intenta de nuevo en un momento.');
    } finally {
      setAiLoading(false);
    }
  };

  // Suggestion chips: 'popular' = top user searches, else manual list (no hardcoded defaults)
  const suggestionChips = useMemo(() => {
    if (settings.offers_suggestions_mode === 'popular') {
      return (popularTerms || []).slice(0, 8);
    }
    try {
      const parsed = JSON.parse(settings.offers_search_suggestions || '[]');
      if (Array.isArray(parsed)) return parsed.filter(Boolean).slice(0, 8);
    } catch (e) { /* ignore */ }
    return [];
  }, [settings.offers_suggestions_mode, settings.offers_search_suggestions, popularTerms]);

  // Admin-configurable search bar look
  const barEmoji = settings.offers_search_emoji || '✨';
  const barBg = settings.offers_bar_bg_color || (darkMode ? '#1e293b' : '#ffffff');
  const barBorder = settings.offers_bar_border_color || (darkMode ? '#334155' : '#e2e8f0');
  const barBtnBg = settings.offers_bar_button_bg || '#2563eb';
  const barBtnText = settings.offers_bar_button_text || '#ffffff';

  // Mercado Libre fallback links (admin-configurable)
  const mlSearchUrl = (settings.ml_search_url_template || 'https://listado.mercadolibre.com.mx/{q}')
    .replace('{q}', encodeURIComponent((searchInput || '').trim()));
  const mlProfileUrl = settings.ml_affiliate_profile_url || '';
  // "Nunca dejar vacío": ofertas populares/visibles para seguir navegando
  const relatedOffers = items.filter((p) => !p.search_only).slice(0, 4);

  // Coupon CTA style (same look as the coupon "Copiar y Canjear" button)
  const couponBtnBg = settings.default_coupon_button_bg || '#fbbf24';
  const couponBtnText = settings.default_coupon_button_text || '#1e293b';
  const fmtMoney = (n) => `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: Number(n) % 1 !== 0 ? 2 : 0 })}`;
  const copyCouponCode = (code) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = code || '';
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (e) { /* ignore */ }
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(code || '').catch(() => {});
    toast.success('Cupón copiado ✅');
  };

  const resetAll = () => {
    setSearchInput('');
    setActiveCat(null);
    setAiSearched(false);
    setAiResults([]);
    setAiRelated([]);
    setAiExplanation('');
    setAiCoupon(null);
    setAiRecommendedId('');
  };

  return (
    <div className={`min-h-screen pb-24 ${!darkMode ? 'bg-slate-50' : ''}`} data-testid="ofertas-page">
      {/* AI Search bar */}
      <form onSubmit={handleSearch} className="mb-2" data-testid="ofertas-search-form">
        <div
          className="flex items-center gap-2 rounded-2xl border p-1.5 pl-3.5 shadow-sm"
          style={{ backgroundColor: barBg, borderColor: barBorder }}
        >
          <span className="text-base flex-shrink-0 leading-none select-none" role="img" aria-label="buscar">{barEmoji}</span>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={settings.offers_search_placeholder || 'Describe lo que buscas. Ej: cel bueno con buena cámara que no pase de 5 mil'}
            data-testid="ofertas-search-input"
            className={`flex-1 bg-transparent outline-none text-sm font-medium ${darkMode ? 'text-slate-100 placeholder:text-slate-500' : 'text-slate-800 placeholder:text-slate-400'}`}
          />
          {(searchInput || aiSearched) && (
            <button
              type="button"
              onClick={resetAll}
              className="p-1.5 rounded-full text-slate-400 hover:text-slate-600"
              data-testid="ofertas-search-clear"
              aria-label="Limpiar"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            type="submit"
            disabled={aiLoading}
            data-testid="ofertas-search-btn"
            className="px-4 py-2 rounded-xl text-sm font-bold active:scale-[0.98] hover:opacity-90 transition-opacity flex-shrink-0 flex items-center gap-1.5 disabled:opacity-60"
            style={{ backgroundColor: barBtnBg, color: barBtnText }}
          >
            {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span>{aiLoading ? 'Buscando' : 'Buscar'}</span>
          </button>
        </div>
      </form>

      {/* Suggested example chips (only in browse mode) */}
      {!aiSearched && suggestionChips.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-3 -mx-1 px-1 no-scrollbar" data-testid="ofertas-suggestions">
          <span className="text-xs font-medium flex-shrink-0 text-slate-400 flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5 text-blue-500" /> Prueba:
          </span>
          {suggestionChips.map((chip, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleSearch(null, chip)}
              data-testid={`ofertas-suggestion-${i}`}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                darkMode
                  ? 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200'
              }`}
            >
              {chip}
            </button>
          ))}
        </div>
      )}

      {/* AI explanation (below the search bar, never inside cards) */}
      {aiSearched && (aiExplanation || aiLoading) && (
        <div
          className={`mb-4 rounded-xl px-3.5 py-2.5 text-xs sm:text-sm leading-snug flex items-start gap-2 ${
            darkMode ? 'bg-blue-950/40 text-blue-200 border border-blue-900' : 'bg-blue-50 text-blue-800 border border-blue-100'
          }`}
          data-testid="ofertas-ai-explanation"
        >
          <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-500" />
          <div className="flex-1 min-w-0">
            <p className="font-medium whitespace-pre-line">
              {aiLoading ? 'Analizando tu búsqueda entre las ofertas disponibles...' : aiExplanation}
            </p>
            {!aiLoading && aiCoupon && (
              <div className="mt-2.5" data-testid="ofertas-ai-coupon">
                <p className="font-semibold">
                  🎟️ Usa el cupón disponible de {fmtMoney(aiCoupon.amount)} y queda en {fmtMoney(aiCoupon.final_price)}
                </p>
                {aiCoupon.code && (
                  <button
                    type="button"
                    onClick={() => copyCouponCode(aiCoupon.code)}
                    data-testid="ofertas-ai-coupon-btn"
                    className="mt-2 w-full h-11 rounded-xl font-extrabold text-xs sm:text-sm flex items-center justify-center gap-1.5 shadow-[0_4px_14px_rgba(0,0,0,0.10)] transition-transform duration-200 active:scale-[0.98] sm:hover:scale-[1.01]"
                    style={{ backgroundColor: couponBtnBg, color: couponBtnText, backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.28), rgba(0,0,0,0.03))' }}
                  >
                    <Ticket className="w-4 h-4" />
                    <span>Ver cupón de {fmtMoney(aiCoupon.amount)}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick access + categories only in browse mode */}
      {!aiSearched && (
        <>
          {quickButtons.length > 0 && (
            <div className="flex gap-2 justify-start overflow-x-auto pb-1 mb-3 -mx-1 px-1 no-scrollbar" data-testid="ofertas-quick-buttons">
              {quickButtons.sort((a, b) => (a.order || 0) - (b.order || 0)).map((btn) => (
                <a
                  key={btn.id}
                  href={btn.url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 px-3 py-2 rounded-lg text-white text-xs sm:text-sm font-medium inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity shadow-sm whitespace-nowrap"
                  style={{ backgroundColor: btn.color || '#3b82f6' }}
                >
                  {btn.icon && <span>{btn.icon}</span>}
                  <span>{btn.text}</span>
                  <ExternalLink className="w-3 h-3 opacity-70" />
                </a>
              ))}
            </div>
          )}

          {categories.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-2 -mx-1 px-1 no-scrollbar" data-testid="ofertas-categories">
              <button
                onClick={() => setActiveCat(null)}
                data-testid="ofertas-cat-all"
                className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors ${
                  activeCat === null
                    ? 'bg-blue-600 text-white'
                    : darkMode ? 'bg-slate-800 text-slate-300 border border-slate-700' : 'bg-white text-slate-600 border border-slate-200'
                }`}
              >
                Todas
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCat(cat.id)}
                  data-testid={`ofertas-cat-${cat.id}`}
                  className={`flex-shrink-0 inline-flex items-center gap-1 px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors ${
                    activeCat === cat.id
                      ? 'bg-blue-600 text-white'
                      : darkMode ? 'bg-slate-800 text-slate-300 border border-slate-700' : 'bg-white text-slate-600 border border-slate-200'
                  }`}
                >
                  {cat.mode === 'price' ? <TagIcon className="w-3 h-3" /> : null}
                  {cat.name}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Results */}
      {aiSearched ? (
        aiLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4" data-testid="ofertas-ai-loading">
            {[1, 2, 3].map((i) => (
              <div key={i} className={`rounded-2xl h-64 animate-pulse ${darkMode ? 'bg-slate-800' : 'bg-slate-200'}`} />
            ))}
          </div>
        ) : aiResults.length === 0 ? (
          <div data-testid="ofertas-empty">
            {/* No exact match: keep the user moving with ML search + popular offers */}
            <div className={`rounded-2xl p-6 text-center border mb-5 ${darkMode ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-white border-slate-200 text-slate-600'}`}>
              <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 ${darkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                <PackageSearch className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-sm font-semibold mb-1">Aún no tenemos justo lo que buscas.</p>
              <p className="text-xs text-slate-400 mb-4">Míralo directo en Mercado Libre o checa estas ofertas que sí tenemos 👇</p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center max-w-md mx-auto">
                <a
                  href={mlSearchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ofertas-ml-search-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-bold active:scale-[0.98] transition-opacity hover:opacity-90"
                  style={{ backgroundColor: '#ffe600', color: '#2d3277' }}
                >
                  <Search className="w-4 h-4" /> Buscar en Mercado Libre
                </a>
                {mlProfileUrl && (
                  <a
                    href={mlProfileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="ofertas-ml-profile-btn"
                    className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold active:scale-[0.98] transition-colors border ${darkMode ? 'bg-slate-700 text-slate-100 border-slate-600 hover:bg-slate-600' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
                  >
                    <ExternalLink className="w-4 h-4" /> Ver mi tienda
                  </a>
                )}
              </div>
            </div>

            {(() => {
              const suggestions = aiRelated.length > 0 ? aiRelated : relatedOffers;
              return suggestions.length > 0 ? (
                <>
                  <p className={`text-sm font-bold mb-3 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>Quizás te interese</p>
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4" data-testid="ofertas-related">
                    {suggestions.map((p) => (
                      <ProductCard key={p.id} coupon={p} onUpdate={() => {}} />
                    ))}
                  </div>
                </>
              ) : null;
            })()}

            <div className="text-center">
              <button
                onClick={resetAll}
                data-testid="ofertas-see-all-btn"
                className={`mt-5 px-5 py-2.5 rounded-xl text-sm font-bold active:scale-[0.98] transition-colors ${darkMode ? 'bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100'}`}
              >
                Ver todas las ofertas
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4" data-testid="ofertas-ai-results">
              {[...aiResults].sort((a, b) => (b.id === aiRecommendedId ? 1 : 0) - (a.id === aiRecommendedId ? 1 : 0)).map((p) => (
                <ProductCard key={p.id} coupon={p} onUpdate={() => {}} recommended={p.id === aiRecommendedId} />
              ))}
            </div>

            {/* Complements / related to sell add-ons even when there are exact results */}
            {aiRelated.length > 0 && (
              <div className="mt-6">
                <p className={`text-sm font-bold mb-3 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>Quizás te interese</p>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4" data-testid="ofertas-related-with-results">
                  {aiRelated.map((p) => (
                    <ProductCard key={p.id} coupon={p} onUpdate={() => {}} />
                  ))}
                </div>
              </div>
            )}

            <div className="text-center">
              <button
                onClick={resetAll}
                data-testid="ofertas-see-all-btn"
                className={`mt-5 px-5 py-2.5 rounded-xl text-sm font-bold active:scale-[0.98] transition-colors ${darkMode ? 'bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100'}`}
              >
                Ver todas las ofertas
              </button>
            </div>
          </>
        )
      ) : loading ? (
        <p className={`text-center py-10 ${darkMode ? 'text-slate-300' : 'text-slate-500'}`}>Cargando ofertas...</p>
      ) : browseList.length === 0 ? (
        <div className={`rounded-2xl p-8 text-center border ${darkMode ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-white border-slate-200 text-slate-600'}`} data-testid="ofertas-empty">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 ${darkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
            <PackageSearch className="w-7 h-7 text-slate-400" />
          </div>
          <p className="text-sm font-semibold">
            {activeCat ? 'No hay ofertas en esta categoría.' : (settings.no_products_message || 'No hay ofertas disponibles por ahora.')}
          </p>
          {activeCat && (
            <button
              onClick={resetAll}
              data-testid="ofertas-see-all-btn"
              className="mt-4 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold active:scale-[0.98] hover:bg-blue-700 transition-colors"
            >
              Ver todas las ofertas
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
          {browseList.map((p) => (
            <ProductCard key={p.id} coupon={p} onUpdate={() => {}} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Ofertas;
